import React, { memo, useEffect, useState } from 'react';
import { Options, Point, PointClickEventObject } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import { HighchartsReact } from 'highcharts-react-official';
import { CHART_HEIGHT_PIXELS, getChartEvents, getCreditsStyle } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import {
    HC_COLORS_MUTED,
    HC_COLORS_STANDARD,
    IOpraChartKey,
    OpraVerticalAlign,
} from '@interfaces/opra-chart.interface';
import { SeriesDataDonut } from '@interfaces-clean/panels.interface';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelTheme } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';

export interface DonutChartProps {
    title?: string;
    entityType?: string;
    panelData: SeriesDataDonut[];
    threeDee?: boolean;
    onClick?: (this: Point, event: PointClickEventObject) => void;
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    secondaryKey?: IOpraChartKey;
    showLegend?: boolean;
    noDataMessage?: string;
    theme?: PanelTheme;
    showWaterMark?: boolean;
    customOptions?: Options;
    customHeight?: number;
}

const styleHeight = { height: '350px' };

const Chart = (props: DonutChartProps) => {
    const {
        panelData,
        threeDee,
        title,
        secondaryKey,
        showLegend,
        onClick,
        entityType,
        noDataMessage,
        theme = PanelTheme.NONE,
        showWaterMark = false,
        chartRef,
        customOptions,
        customHeight,
    } = props;

    const [options, setOptions] = useState<Options | null>(null);
    const [hasData, setHasData] = useState(true);
    const { t } = useTranslation();

    useEffect(() => {
        const totalValues = panelData.reduce<number>((total: number, plot: SeriesDataDonut) => {
            return total + plot.count;
        }, 0);

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setHasData(totalValues > 0);
        setOptions(
            Highcharts.merge(
                {
                    colors: getThemeColours(theme),
                    credits: showWaterMark ? getCreditsStyle(`${t('cm:waterMarkMainPanelText')}`) : { enabled: false },
                    chart: {
                        type: 'pie',
                        options3d: threeDee
                            ? {
                                  enabled: true,
                                  alpha: 45,
                              }
                            : undefined,
                        height: customHeight ?? CHART_HEIGHT_PIXELS,
                        events: getChartEvents(showWaterMark, false, title, undefined, {
                            fullScreenOptions: {
                                legend: {
                                    width: 300,
                                    maxHeight: 500,
                                    itemStyle: {
                                        fontSize: '32px',
                                    },
                                },
                            },
                            defaultOptions: {
                                legend: {
                                    maxHeight: 200,
                                    width: 200,
                                    verticalAlign: 'middle',
                                    layout: 'vertical',
                                    align: 'right',
                                    itemStyle: {
                                        fontSize: undefined,
                                    },
                                },
                                chart: {
                                    height: CHART_HEIGHT_PIXELS,
                                    width: null,
                                },
                            },
                            printOptions: {
                                legend: {
                                    verticalAlign: 'bottom',
                                    align: 'center',
                                    maxHeight: 500,
                                    width: 450,
                                },
                                chart: {
                                    height: 800,
                                    width: 600,
                                },
                            },
                        }),
                    },
                    exporting: {
                        chartOptions: {
                            title: { text: title, style: { fontWeight: 'semi-bold' } },
                            credits: { enabled: false },
                            legend: {
                                align: 'center',
                                verticalAlign: 'bottom',
                                width: 550,
                                maxHeight: 400,
                            },
                            chart: {
                                height: 800,
                                width: 600,
                            },
                        },
                        enabled: !chartRef,
                        fallbackToExportServer: false,
                        buttons: {
                            contextButton: {
                                enabled: false,
                            },
                        },
                    },
                    subtitle:
                        secondaryKey === undefined
                            ? undefined
                            : {
                                  text: secondaryKey.text,
                                  useHTML: true,
                                  verticalAlign:
                                      secondaryKey.position === OpraVerticalAlign.top
                                          ? 'top'
                                          : secondaryKey.position === OpraVerticalAlign.middle
                                            ? 'middle'
                                            : 'bottom',
                                  style: { display: (showLegend ?? true) ? undefined : 'none' },
                              },
                    legend: {
                        enabled: showLegend,
                        itemStyle: {
                            cursor: 'pointer',
                            textOverflow: 'ellipsis',
                        },
                        layout: 'vertical',
                        align: 'right',
                        maxHeight: 200,
                        verticalAlign: 'middle',
                        width: 200,
                    },
                    tooltip: {
                        pointFormatter() {
                            const that: Highcharts.Point = this;
                            return `${that.y} ${that.series.name} of ${totalValues}`;
                        },
                        headerFormat: '<span><b>{point.key}:</b> {point.percentage:.0f}%</span><br/>',
                    },
                    plotOptions: {
                        pie: {
                            dataLabels: {
                                enabled: false,
                                distance: -30,
                                style: { opacity: 1 }, // Forces all labels to show, even when HighCharts deems to hide them
                                formatter() {
                                    // TODO: review for ESLint 10 - eslint-disable-next-line react/no-this-in-sfc
                                    return this.y && this.y > 0 ? this.y : null;
                                },
                            },
                            showInLegend: true,
                            innerSize: 100,
                            depth: 45,
                            point: onClick ? { events: { click: onClick } } : undefined,
                            cursor: onClick ? 'pointer' : undefined,
                        },
                    },
                    series: [
                        {
                            type: 'pie',
                            name: entityType || '',
                            data: panelData.map((plot: SeriesDataDonut) => ({
                                name: plot.name,
                                y: plot.count,
                                color:
                                    plot.color === undefined
                                        ? undefined
                                        : threeDee
                                          ? HC_COLORS_STANDARD[plot.color]
                                          : HC_COLORS_MUTED[plot.color],
                            })),
                        },
                    ],
                },
                customOptions,
            ),
        );
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [props]);

    if (options === null) return <Loading />;

    return hasData || !noDataMessage ? (
        <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />
    ) : (
        <div style={styleHeight}>{noDataMessage}</div>
    );
};
export const DonutChart = memo(Chart);
